<template>
  <ContentSection spacing="none">
    <Container>
      <div v-if="data.show_header" class="bg-gray-10 px-7 pb-9 pt-4">
        <Breadcrumbs class="mb-2" />
        <h1
          class="text-md font-bold md:text-xl"
          v-html="getTranslatedProperty(productCategory, 'name')"
        />
      </div>
      <ShopListingGrid
        :category="productCategory"
        :category-tree="tree ?? undefined"
        :enable-filters="data.enable_filters"
        class="my-8"
      >
        <template v-if="data.sidebar_title_option.value !== 'category'" #title>
          <h3 class="px-4 pb-4 text-sm font-bold text-gray-800">
            <template v-if="data.sidebar_title_option.value === 'custom'">
              {{ data.sidebar_title }}
            </template>
            <template v-else-if="parentCategory">
              {{ getTranslatedProperty(parentCategory, 'name') }}
            </template>
          </h3>
        </template>
      </ShopListingGrid>
    </Container>
  </ContentSection>
</template>

<script setup lang="ts">
import { getTranslatedProperty } from '@shopware/helpers'
import type { Schemas } from '#shopware'
import {
  BrandsCategoryRootId,
  ExclusiveBrandsCategoryRootId,
} from '~/types/general'

const props = defineProps<{
  category: Schemas['Category']
  data: {
    root?: string[]
    override_product_category_ids?: string[]
    enable_filters: boolean
    show_header: boolean
    sidebar_title_option: {
      value: 'category' | 'parent_category' | 'custom'
    }
    sidebar_title?: string
  }
}>()

const { advancedSearch: searchCategories, search } = useCategorySearch()

const { data: productCategory } = await useAsyncData(
  'product-category',
  async () => {
    if (
      props.data.override_product_category_ids &&
      props.data.override_product_category_ids.length
    ) {
      return await search(props.data.override_product_category_ids[0], {
        query: {
          associations: {
            parent: {},
            media: {},
            seoUrls: {},
          },
        },
      })
    } else {
      return Promise.resolve(props.category)
    }
  }
)

const isBrand = computed(() =>
  productCategory.value?.path?.includes(BrandsCategoryRootId)
)

const isExclusiveBrand = computed(() =>
  productCategory.value?.path?.includes(ExclusiveBrandsCategoryRootId)
)

const categoryTreeRootId = computed(() => {
  if (props.data.root && props.data.root.length) {
    return props.data.root[0]
  }
  if (isBrand.value || isExclusiveBrand.value) {
    if (productCategory.value?.customFields?.novu_jp_custom_categories_brand) {
      return productCategory.value.id
    } else {
      return productCategory.value.parentId
    }
  }
  const items = (
    productCategory.value.path?.replace(/^\|/, '')?.split('|') ?? []
  ).filter(Boolean)
  if (items.length > 2) {
    return items[1]
  } else if (items.length === 1) {
    return productCategory.value.id
  } else {
    return productCategory.value.parentId ?? productCategory.value.id
  }
})

const listingTreePromise = useAsyncData('product-listing-tree', () =>
  searchCategories({
    query: {
      associations: {
        seoUrls: {},
        children: {
          associations: {
            seoUrls: {},
          },
        },
      },
      filter: [
        {
          type: 'equals',
          field: 'parentId',
          value: categoryTreeRootId.value,
        },
      ],
    },
  })
)

const parentCategoryPromise = useAsyncData('parent', () => {
  if (props.data.sidebar_title_option.value === 'parent_category') {
    return search(categoryTreeRootId.value, {
      query: {
        fields: ['id', 'name'],
      },
    })
  } else {
    return Promise.resolve(null)
  }
})

const { data: tree } = await listingTreePromise
const { data: parentCategory } = await parentCategoryPromise
</script>
